import React, { FC, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import dayjs from "dayjs";

import DataTable, { createDataRow, HeadCell } from "@/components/DataTable";
import ActionMenu from "@/components/ActionMenu";
import GenericAlertModal from "@/components/AlertModal";

import useDebounce from "@/hooks/useDebounce";
import { usePageStore } from "@/hooks/usePageStore";
import { useUserStore } from "@/hooks/useUserStore";
import {
  useAuditLogFirstRender,
  useCreateAuditlog,
} from "@/hooks/useAuditLogs";
import {
  useArchiveCategory,
  useCategories,
  useCreateCategory,
  useUpdateCategory,
} from "@/hooks/useCategories";

import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import { useAppTheme } from "@/utils/theme";
import { toTitleCase } from "@/utils/toTitleCase";

import {
  Category,
  CategoryElement,
  CategoryPayload,
} from "@/domain/category.interface";
import { ErrorResponse } from "@/domain/error.interface";
import { ACTION_CODE, MODULE_NAME, OPTIONS } from "@/domain/auditlog.enum";

import CategoryFilters, { Filters } from "@/ui/content/categories/Filters";
import CategoryModal from "@/ui/modals/categories/CategoryModal";

const DEFAULT_FILTERS: Filters = {
  sortField: "type",
  sortOrder: 1,
  pageNumber: 0,
  pageSize: 10,
  search: "",
  status: "",
  type: "",
};

const pickItemName = (item: CategoryElement) => {
  const langs = item.languages || [];
  if (langs.length === 0) return "";
  const en = langs.find((l) => l.language?.toLowerCase().startsWith("en"));
  return (en || langs[0]).name || "";
};

const CategoriesPage: FC = () => {
  const theme = useAppTheme();
  const { showSnackBar } = useFeedBack();
  const { userInfo } = useUserStore();
  const { setPageTitle } = usePageStore();

  const filtersState = useState<Filters>(DEFAULT_FILTERS);
  const [filters, setFilters] = filtersState;
  const debouncedSearch = useDebounce({ value: filters.search });

  const [modalState, setModalState] = useState<{
    open: boolean;
    category?: Category;
  }>({ open: false });

  const [deleteAlert, setDeleteAlert] = useState<{
    open: boolean;
    category?: Category;
  }>({ open: false });

  const {
    categories,
    isLoadingCategories,
    isFetchingCategories,
    refetchCategories,
  } = useCategories({
    sortField: filters.sortField,
    sortOrder: filters.sortOrder,
    pageNumber: filters.pageNumber,
    pageSize: filters.pageSize,
    search: debouncedSearch,
    status: filters.status,
    type: filters.type,
  });

  const createCategory = useCreateCategory();
  const updateCategory = useUpdateCategory();
  const archiveCategory = useArchiveCategory();
  const createAuditLog = useCreateAuditlog();

  const isSaving = createCategory.isPending || updateCategory.isPending;

  useEffect(() => {
    setPageTitle("Categories");
  }, []);

  useAuditLogFirstRender({
    appType: "WEB_BACK_OFFICE",
    module: MODULE_NAME.CATEGORIES,
    option: OPTIONS.CATEGORIES_LIST,
    actionCode: ACTION_CODE.WEB_CAT_LIST_VIEW,
    action: "When entering",
    detail: "Viewed the list of categories",
    transactionDate: new Date(),
    accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
    createdBy: userInfo?._id as string,
    programId: userInfo?.programId as string,
    orgUnitId: userInfo?.orgUnitIds?.[0] as string,
  });

  useEffect(() => {
    refetchCategories();
  }, [
    debouncedSearch,
    filters.status,
    filters.type,
    filters.sortField,
    filters.sortOrder,
    filters.pageNumber,
    filters.pageSize,
  ]);

  useEffect(() => {
    if (debouncedSearch !== "") {
      createAuditLog.mutate({
        appType: "WEB_BACK_OFFICE",
        module: MODULE_NAME.CATEGORIES,
        option: OPTIONS.CATEGORIES_LIST,
        actionCode: ACTION_CODE.WEB_CAT_LIST_SEARCH,
        action: "When searching",
        detail: "Searched within the list of categories",
        transactionDate: new Date(),
        accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
        createdBy: userInfo?._id as string,
      });
    }
  }, [debouncedSearch]);

  const handleOpenCreate = () => {
    setModalState({ open: true, category: undefined });
  };

  const handleOpenEdit = (category: Category) => {
    setModalState({ open: true, category });
  };

  const handleCloseModal = () => {
    setModalState({ open: false, category: undefined });
  };

  const handleSave = async (data: CategoryPayload) => {
    const editing = modalState.category;

    try {
      if (editing) {
        await updateCategory.mutateAsync({ id: editing._id, payload: data });

        createAuditLog.mutate({
          appType: "WEB_BACK_OFFICE",
          module: MODULE_NAME.CATEGORIES,
          option: OPTIONS.CATEGORY_ADD_EDIT,
          actionCode: ACTION_CODE.WEB_CAT_EDIT,
          action: "When editing a category",
          detail: `Edited category ${data.type}`,
          transactionDate: new Date(),
          accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
          createdBy: userInfo?._id as string,
        });

        showSnackBar("The category has been updated.", "success");
      } else {
        await createCategory.mutateAsync(data);

        createAuditLog.mutate({
          appType: "WEB_BACK_OFFICE",
          module: MODULE_NAME.CATEGORIES,
          option: OPTIONS.CATEGORY_ADD_EDIT,
          actionCode: ACTION_CODE.WEB_CAT_ADD,
          action: "When creating a category",
          detail: `Created category ${data.type}`,
          transactionDate: new Date(),
          accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
          createdBy: userInfo?._id as string,
        });

        showSnackBar("The category has been created.", "success");
      }

      handleCloseModal();
      refetchCategories();
    } catch (error: unknown) {
      const err = error as ErrorResponse;
      showSnackBar(
        (Array.isArray(err.message) ? err.message[0] : err.message) ||
          err.error ||
          "An error occurred"
      );
    }
  };

  const handleConfirmDelete = async () => {
    const target = deleteAlert.category;
    if (!target) return;

    try {
      await archiveCategory.mutateAsync(target._id);

      createAuditLog.mutate({
        appType: "WEB_BACK_OFFICE",
        module: MODULE_NAME.CATEGORIES,
        option: OPTIONS.DELETE,
        actionCode: ACTION_CODE.WEB_CAT_DELETE,
        action: "When deleting a category",
        detail: `Deleted category ${target.type}`,
        transactionDate: new Date(),
        accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
        createdBy: userInfo?._id as string,
      });

      showSnackBar("The registry has been deleted.", "success");
      setDeleteAlert({ open: false });
      refetchCategories();
    } catch (error: unknown) {
      const err = error as ErrorResponse;
      showSnackBar(
        (Array.isArray(err.message) ? err.message[0] : err.message) ||
          err.error ||
          "An error occurred"
      );
    }
  };

  const headCells: HeadCell[] = [
    {
      id: "type",
      isSortable: true,
      disablePadding: true,
      label: "Type",
      align: "left",
      width: "200px",
    },
    {
      id: "items",
      isSortable: false,
      disablePadding: false,
      label: "Items",
      align: "left",
    },
    {
      id: "status",
      isSortable: true,
      disablePadding: false,
      label: "Status",
      align: "left",
      width: "120px",
    },
    {
      id: "createdAt",
      isSortable: true,
      disablePadding: false,
      label: "Created",
      align: "left",
      width: "150px",
    },
    {
      id: "action",
      isSortable: false,
      disablePadding: false,
      label: " ",
      align: "right",
      width: "80px",
    },
  ];

  const rows = useMemo(() => {
    if (!categories || categories.list.length === 0) return [];

    return categories.list.map((row, index) => {
      const actionMenu = [
        {
          value: "Edit",
          onClick: () => handleOpenEdit(row),
          icon: <EditIcon fontSize="small" />,
        },
        {
          value: "Delete",
          onClick: () => setDeleteAlert({ open: true, category: row }),
          icon: <DeleteOutlineIcon fontSize="small" />,
          sx: {
            color: theme.palette.error.main,
          },
        },
      ];

      const itemNames = (row.categories || [])
        .map((item) => pickItemName(item))
        .filter(Boolean);

      return createDataRow(index, {
        type: <div>{row.type || "-"}</div>,
        items:
          itemNames.length > 0 ? (
            <Stack
              direction="row"
              spacing={0.5}
              useFlexGap
              flexWrap="wrap"
              sx={{ rowGap: 0.5 }}
            >
              {itemNames.map((name, i) => (
                <Chip
                  key={`${row._id}-item-${i}`}
                  size="small"
                  label={<div className="text-xs">{name}</div>}
                />
              ))}
            </Stack>
          ) : (
            <div>-</div>
          ),
        status: row.status ? (
          <Chip
            sx={{
              bgcolor:
                row.status === "ACTIVE"
                  ? theme.palette.brand.light
                  : theme.palette.warning.light,
              borderWidth: 1,
              borderStyle: "solid",
              borderColor:
                row.status === "ACTIVE"
                  ? theme.palette.brand.main
                  : theme.palette.warning.dark,
            }}
            label={<div className="text-xs">{toTitleCase(row.status)}</div>}
          />
        ) : (
          <div>-</div>
        ),
        createdAt: (
          <div>
            {row.createdAt ? dayjs(row.createdAt).format("DD MMM YYYY") : "-"}
          </div>
        ),
        action: <ActionMenu id={row._id} options={actionMenu} />,
      });
    });
  }, [categories, theme]);

  return (
    <>
      <div className="w-full overflow-y-auto scroll-smooth">
        <div className="full-width-container mx-auto">
          <div className="flex flex-col items-center justify-start min-h-screen space-y-5">
            <Card sx={{ width: "100%", borderRadius: "8px" }}>
              <CardContent
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  rowGap: "20px",
                }}
              >
                <Box
                  sx={{ width: "100%" }}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="headlineSmall" component="h1">
                    Categories
                  </Typography>

                  <Button
                    size="medium"
                    onClick={handleOpenCreate}
                    variant="contained"
                    sx={{
                      borderRadius: "8px",
                      textTransform: "none",
                      textAlign: "center",
                      height: "40px",
                      backgroundColor: theme.palette.primary.dark,
                      color: theme.palette.textMain.light,
                    }}
                    startIcon={<AddIcon />}
                  >
                    ADD
                  </Button>
                </Box>

                <CategoryFilters filtersState={filtersState} />

                <DataTable
                  data={rows}
                  headCells={headCells}
                  order={filters.sortOrder === 1 ? "asc" : "desc"}
                  orderBy={filters.sortField}
                  rowsPerPage={filters.pageSize}
                  page={filters.pageNumber}
                  total={categories?.totalCount}
                  isLoading={isLoadingCategories || isFetchingCategories}
                  messageEmptyData="No categories to show."
                  onChangePage={(_event, newPage) => {
                    setFilters((prev) => ({ ...prev, pageNumber: newPage }));
                  }}
                  onChangeSize={(event) => {
                    setFilters((prev) => ({
                      ...prev,
                      pageNumber: 0,
                      pageSize: Number(event.target.value),
                    }));
                  }}
                  onChangeSort={(order, orderBy) => {
                    setFilters((prev) => {
                      const newSortField = orderBy as string;
                      const newSortOrder = order === "asc" ? 1 : -1;
                      if (
                        prev.sortField === newSortField &&
                        prev.sortOrder === newSortOrder
                      ) {
                        return prev;
                      }

                      return {
                        ...prev,
                        pageNumber: 0,
                        sortField: newSortField,
                        sortOrder: newSortOrder,
                      };
                    });
                  }}
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {modalState.open && (
        <CategoryModal
          open={modalState.open}
          onClose={handleCloseModal}
          category={modalState.category}
          onSave={handleSave}
          isPending={isSaving}
        />
      )}

      {deleteAlert.open && (
        <GenericAlertModal
          onCancel={() => setDeleteAlert({ open: false })}
          onClick={handleConfirmDelete}
          title="Delete"
          description="Are you sure you want to delete this registry?"
          submitText="Confirm"
          cancelText="Cancel"
          isLoading={archiveCategory.isPending}
          theme={theme}
        />
      )}
    </>
  );
};

export default CategoriesPage;
